import "./transaction-form.scss";

import * as React from "react";

import {cx}              from "emotion";

import {cst, cstAssign}   from "@Common/react/chain-services";
import {UseState}         from "@Common/react/use-state";
import {Types}            from "@Common/types";
import {
    DELIVERY_TYPE_VALUES,
    ORDER_STATUS_VALUES,
}                         from "@Common/types/apis/payloads/next/reporting.transaction.option-values";
import {Dropdown}         from "@Common/ui-components/dropdown/dropdown";
import {SearchableSelect} from "@Common/ui-components/searchable-select/searchable-select";
import {chain}            from "@Common/utils/fs";
import {upperCase1}       from "@Common/utils/strings";
import {Responsive}       from "@Common/ui-components/responsive";

import {formatDate}       from "@Xpresso/routes/common/calendar-ui/format-date";
import {PickerPanel}      from "@Xpresso/routes/common/date-range-picker/picker-ui/picker-panel";

type FormModel = Types.Xpresso.Account.Admin.Reporting.Transaction.FormModel;

export const AccountTransactionForm = ({
    agents,
    criteria,
    maxDate,
    minDate,
    onGenerateReport,
    programs,
    setFieldValue,
    showOfficeInput,
    showRegionInput,
    validationError,
    responsive,
    value    : {
        agentId,
        branchId,
        deliveryType,
        emailAddresses,
        endDate,
        options: fields,
        orderStatus,
        programId,
        regionId,
        startDate,
    },
    branches          = [],
    regions           = [],
}:{
    agents            : Array<Pick<Types.Apis.Models.Agent, "idAgent" | "agentNameFirst" | "agentNameLast">>,
    branches         ?: Array<Pick<Types.Apis.Models.Branch, "idBranch" | "branchName">>,
    criteria          : Array<{label: string, value: FormModel['options'][number]}>,
    maxDate           : Types.Common.Utils.DateObject.DateObject,
    minDate           : Types.Common.Utils.DateObject.DateObject,
    onGenerateReport  : () => void,
    programs          : Array<{idProgram: string, name: string}>,
    regions          ?: Array<Pick<Types.Apis.Models.Region, "idRegion" | "name">>,
    setFieldValue     : <T extends keyof FormModel>(field: T, value: FormModel[T]) => void,
    showOfficeInput   : boolean,
    showRegionInput   : boolean,
    validationError  ?: string,
    responsive        : any,
    value             : Types.Xpresso.Account.Admin.Reporting.Transaction.FormModel,    
}) => cst<{
    responsive        : any,
}>(
    // @ts-ignore
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({
        responsive,
    }) => (
        <div className="account-transaction-form-46s6">
            <section>
                { !responsive.ge("md") && <label className="header-mobile">Detailed Transaction Report</label> }
                <div className={cx("field-box", {mobile: !responsive.ge("md")})}>                
                    <div className="large-cols field-row">                    
                        {showRegionInput && (
                            <div className="field">
                                { responsive.ge("md") && <label>Select Region</label> }
                                <SearchableSelect
                                    allowClear={true}
                                    getLabel={(r) => r.name}
                                    isSelected={(r) => regionId === r.idRegion}
                                    list={regions}
                                    onSelect={(r) => setFieldValue("regionId", r?.idRegion)}
                                    placeholder= { responsive.ge("md") ? "All regions" : "Select Region" }
                                    xdQaName="Region"
                                />
                            </div>
                        )}
                        <div className="field">
                        { responsive.ge("md") && <label>Date Range</label> }
                            <DateRangePicker
                                endDate={endDate}
                                maxDate={maxDate}
                                minDate={minDate}
                                setEndDate={(v) => setFieldValue("endDate", v)}
                                setStartDate={(v) => setFieldValue("startDate", v)}
                                startDate={startDate}
                                isDesktop={responsive.ge("md")}
                            />
                        </div>
                        {showOfficeInput && (
                            <div className="field">
                                { responsive.ge("md") && <label>Select Office</label> }
                                <SearchableSelect
                                    allowClear={true}
                                    getLabel={(b) => b.branchName}
                                    isSelected={(b) => branchId === b.idBranch}
                                    list={branches}
                                    onSelect={(b) => setFieldValue("branchId", b?.idBranch)}
                                    placeholder= { responsive.ge("md") ? "All offices" : "Select Office" }
                                    xdQaName="Office"
                                />
                            </div>
                        )}
                        <div className="field">
                        { responsive.ge("md") && <label>Select User</label> }
                            <SearchableSelect
                                allowClear={true}
                                getLabel={a => [a.agentNameFirst, a.agentNameLast].join(" ")}
                                isSelected={(a) => agentId === a.idAgent}
                                list={agents}
                                onSelect={(a) => setFieldValue("agentId", a?.idAgent)}
                                placeholder= { responsive.ge("md") ? "All users" : "Select User(s)" }
                                xdQaName="User"
                            />
                        </div>
                    </div>

                    <hr/>

                    <div className="smol-cols field-row">
                        <div className="field">
                            <label>Order Status</label>
                            <SearchableSelect<FormModel['orderStatus']>
                                getLabel={upperCase1}
                                isSelected={(v) => v === orderStatus}
                                list={ORDER_STATUS_VALUES}
                                onSelect={(v) => setFieldValue("orderStatus", v)}
                                placeholder=""
                                xdQaName="Order Status"
                            />
                        </div>
                        { responsive.ge("md") ?           
                           ( <section>
                              <div className="field">
                                <label>Delivery Type</label>
                                <SearchableSelect
                                    getLabel={(v) => v === "pickedUp" ? "Pickup" : upperCase1(v)}
                                    isSelected={(v) => v === deliveryType}
                                    list={DELIVERY_TYPE_VALUES}
                                    onSelect={(v) => setFieldValue("deliveryType", v)}
                                    placeholder=""
                                    xdQaName="Delivery Type"
                            />
                                </div>
                                <div className="field">
                                <label>Program</label>
                                <SearchableSelect
                                    getLabel={(p) => p.name}
                                    isSelected={(p) => programId === p?.idProgram}
                                    list={programs}
                                    onSelect={(p) => setFieldValue("programId", p?.idProgram)}
                                    placeholder=""
                                    xdQaName="Program"
                            />
                                </div> 
                             </section>                               
                            ) : (
                           <div className="field-mobile">
                                  <div className="field field_left">
                                    <label>Delivery All</label>
                                    <SearchableSelect
                                        getLabel={(v) => v === "pickedUp" ? "Pickup" : upperCase1(v)}
                                        isSelected={(v) => v === deliveryType}
                                        list={DELIVERY_TYPE_VALUES}
                                        onSelect={(v) => setFieldValue("deliveryType", v)}
                                        placeholder=""
                                    />
                                    </div>
                                    <div className="field field_right">
                                    <label>Program</label>
                                    <SearchableSelect
                                        getLabel={(p) => p.name}
                                        isSelected={(p) => programId === p?.idProgram}
                                        list={programs}
                                        onSelect={(p) => setFieldValue("programId", p?.idProgram)}
                                        placeholder=""
                                    />
                                    </div>
                                </div>                    
                            ) }                        
                    </div>
                </div>
            </section>

            <hr/>

            <section>
                <header>
                    <h4>Report Criteria</h4>
                    {chain(
                        fields?.length === criteria.length,
                        (allSelected) => (
                            <a onClick={() => setFieldValue("options", allSelected ? [] : criteria.map(o => o.value))} xd-qa-type="link" xd-qa-name={allSelected ? "Clear selection" : "Select All"}>
                                {allSelected ? "Clear selection" : "Select All"}
                            </a>
                        )
                    )}
                </header>
                <div className={cx("criteria-grid", {mobile: !responsive.ge("md")})}>
                    {criteria.map((option) => {
                        const checked = fields?.includes(option.value) ?? false;
                        return (
                            <label key={option.value} className={cx({active: checked})} xd-qa-type="button" xd-qa-name={checked ? option.label + " checked" : option.label}>
                                {option.label}
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                        const valuesMinusCurrent = fields?.filter(v => v !== option.value) ?? [];
                                        setFieldValue("options", e.target.checked ? valuesMinusCurrent.concat(option.value) : valuesMinusCurrent);
                                    }}
                                    xd-qa-type="icon"
                                    xd-qa-name="check"
                                />
                            </label>
                        );
                    })}
                </div>
            </section>

            <hr/>

            <section>
                <header xd-qa-type="label" xd-qa-name="Email Address"><h4>Email Address</h4></header>
                <div className="email-ui">
                    <div className="field">
                        <input
                            onBlur={() => setFieldValue("emailAddresses", emailAddresses?.filter(Boolean) ?? [])}
                            onChange={(e) => setFieldValue("emailAddresses", e.target.value.split(/[\s,]+/))}
                            onKeyDown={(e) => {
                                if(e.key === "Backspace")
                                    setFieldValue("emailAddresses", emailAddresses?.filter(Boolean));
                            }}
                            value={emailAddresses?.join(", ") ?? ""}
                            xd-qa-type="textbox"
                            xd-qa-name="email"
                        />
                        <small>You can add multiple addresses, separated by comma</small>
                    </div>
                    <button
                        disabled={Boolean(validationError)}
                        onClick={onGenerateReport}
                        aria-label={validationError ?? undefined}
                        xd-qa-type="button"
                        xd-qa-name="Generate Report"
                    >
                        Generate Report
                    </button>
                </div>
            </section>
        </div>
    ),
);

const DateRangePicker = ({endDate, maxDate, minDate, setEndDate, setStartDate, startDate, isDesktop}:{
    endDate      : Types.Common.Utils.DateObject.DateObject,
    maxDate      : Types.Common.Utils.DateObject.DateObject,
    minDate      : Types.Common.Utils.DateObject.DateObject,
    setEndDate   : (v:Types.Common.Utils.DateObject.DateObject) => void,
    setStartDate : (v:Types.Common.Utils.DateObject.DateObject) => void,
    startDate    : Types.Common.Utils.DateObject.DateObject,
    isDesktop    : boolean,
}) => cst<{
    focusState : Types.Common.React.UseState.UseStateNext<"start" | "end">,
}>(
    cstAssign("focusState", ({}, next) => UseState({next, initValue: "start"})),
    ({focusState}) => Dropdown({
        forcedExpandBottom : true,
        minExpandHeight    : 480,
        renderExpand       : ({close, domRef, position}) => PickerPanel({
            domRef,
            focus: focusState,
            maxDate,
            minDate,
            position,
            endDate : {
                onChange : setEndDate,
                value    : endDate,
            },
            onClear: () => {
                setEndDate(null);
                setStartDate(null);
            },
            onClose: close,
            startDate : {
                onChange : setStartDate,
                value    : startDate,
            },
        }),
        renderToggle       : ({domRef, showExpand}) => (
            <div ref={domRef} onClick={() => showExpand()} className={cx("datepicker-toggle", {empty: !startDate || !endDate})} xd-qa-type="dropdown" xd-qa-name="Date Picker">
                {!startDate || !endDate ? ( isDesktop ? "Click to select dates" : "Date Range" ): formatDateRange(startDate, endDate)}
                <i className="icon fa fa-calendar" xd-qa-type="icon" xd-qa-name="calendar" />
            </div>
        )
    }),
);

const formatDateRange = (start:Types.Common.Utils.DateObject.DateObject, end:Types.Common.Utils.DateObject.DateObject): string => (
    [start, end]
        .map((date) => formatDate(date))
        .join(" to ")
);